// Confirmación de cierre a 100% al soltar sobre esa columna (2026-09-22, ver
// docs/modulos/pizarra-ventas.md "El 100% — mover UNA, no el grupo"). Presentacional puro: no
// conoce `op`/`claveOportunidad` ni ninguna estructura de la Pizarra — recibe `items` ya resueltos
// ({ clave, campana, monto }, claves como strings opacos) y devuelve claves al confirmar. Mismo
// patrón de modal que el resto del hub (reactstrap Modal/ModalHeader/ModalBody/ModalFooter,
// VentasDetalle.showConfirmationModal).
import { useEffect, useState } from "react";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";

/**
 * @param {{
 *   isOpen: boolean,
 *   nombreCliente: string,
 *   items: Array<{ clave: string, campana: string, monto: string }>,
 *   ganadoraDefaultClave: string,
 *   onCancel: () => void,
 *   onConfirm: (ganadoraClave: string, eliminarClaves: Set<string>) => void,
 * }} props
 */
export const ConfirmarCierre100Modal = ({
  isOpen,
  nombreCliente,
  items,
  ganadoraDefaultClave,
  onCancel,
  onConfirm,
}) => {
  const [ganadoraClave, setGanadoraClave] = useState(ganadoraDefaultClave);
  // Overrides EXPLÍCITOS del usuario sobre "eliminar" (clave -> boolean) — sin tocar, el default es
  // "marcado" para toda propuesta que NO sea la ganadora elegida (dos propuestas del mismo cliente
  // en la misma columna no siempre son alternativas de la misma negociación, de ahí la posibilidad
  // de desmarcar puntualmente).
  const [overrides, setOverrides] = useState(() => new Map());

  // Reset al abrir con un grupo nuevo (o al reabrir) — el modal es el mismo componente montado una
  // vez, no una instancia nueva por apertura.
  useEffect(() => {
    if (isOpen) {
      setGanadoraClave(ganadoraDefaultClave);
      setOverrides(new Map());
    }
  }, [isOpen, ganadoraDefaultClave]);

  if (!isOpen) return null;

  const eliminarMarcado = (clave) => (overrides.has(clave) ? overrides.get(clave) : clave !== ganadoraClave);

  const toggleEliminar = (clave) => {
    setOverrides((prev) => {
      const next = new Map(prev);
      next.set(clave, !eliminarMarcado(clave));
      return next;
    });
  };

  const handleConfirmar = () => {
    const eliminarClaves = new Set(
      items.filter((it) => it.clave !== ganadoraClave && eliminarMarcado(it.clave)).map((it) => it.clave)
    );
    onConfirm(ganadoraClave, eliminarClaves);
  };

  const esMultiple = items.length > 1;
  const ganadora = items.find((it) => it.clave === ganadoraClave) || items[0];
  const otras = items.filter((it) => it.clave !== ganadoraClave);

  return (
    <Modal isOpen={isOpen} toggle={onCancel}>
      <ModalHeader toggle={onCancel}>{esMultiple ? "¿Cuál se ganó?" : "Confirmar cierre a 100%"}</ModalHeader>
      <ModalBody>
        {esMultiple ? (
          <>
            <p>
              <strong>{nombreCliente}</strong> tiene {items.length} propuestas en esta columna — elegí cuál se ganó.
              Solo esa pasa al 100%.
            </p>
            {items.map((it) => (
              <div key={it.clave} className="form-check" style={{ marginBottom: 6 }}>
                <input
                  className="form-check-input"
                  type="radio"
                  name="ganadora-100"
                  id={`ganadora-${it.clave}`}
                  checked={ganadoraClave === it.clave}
                  onChange={() => setGanadoraClave(it.clave)}
                />
                <label className="form-check-label" htmlFor={`ganadora-${it.clave}`}>
                  {it.campana} — {it.monto}
                </label>
              </div>
            ))}
            {otras.length > 0 && (
              <>
                <hr />
                <p style={{ marginBottom: 6 }}>
                  Las demás se dan de baja (no pasan a 0% — el 0% es memoria de intentos que no salieron, y estas no lo
                  fueron: se ganó otra del mismo grupo). Desmarcá si alguna en realidad no es una alternativa de la
                  misma negociación.
                </p>
                {otras.map((it) => (
                  <div key={it.clave} className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`eliminar-${it.clave}`}
                      checked={eliminarMarcado(it.clave)}
                      onChange={() => toggleEliminar(it.clave)}
                    />
                    <label className="form-check-label" htmlFor={`eliminar-${it.clave}`}>
                      Eliminar — {it.campana} — {it.monto}
                    </label>
                  </div>
                ))}
              </>
            )}
          </>
        ) : (
          <p>
            ¿Confirmás el cierre de <strong>{nombreCliente}</strong> — {ganadora?.campana} — {ganadora?.monto}?
          </p>
        )}
        <p className="text-muted" style={{ fontSize: "0.85rem", marginTop: 12, marginBottom: 0 }}>
          Al confirmar se dispara la fecha de cierre y la venta entra en comisiones y en proyección de facturación.
        </p>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onCancel}>
          Cancelar
        </Button>
        <Button color="primary" onClick={handleConfirmar}>
          Confirmar cierre
        </Button>
      </ModalFooter>
    </Modal>
  );
};
